import React, { useCallback, useEffect, useState } from 'react';
import { Flame, Check } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * Floating coal-round reminder banner.
 *
 * The backend's 40-min coal_check_shift_loop stamps an unack'd "round" doc
 * each cycle. This banner polls /api/coal-round/current every 30s, surfaces
 * itself when an unacknowledged round exists, and lets waiter/manager tap
 * Done to dismiss it until the next 40-min cycle fires.
 *
 * Mounted at app-shell level for waiter/manager only.
 */
export default function CoalRoundBanner() {
  const { user } = useAuth();
  const eligible = ['waiter', 'manager', 'director'].includes(user?.role);
  const [round, setRound] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchCurrent = useCallback(async () => {
    if (!eligible) return;
    try {
      const { data } = await api.get('/coal-round/current');
      setRound(data && !data.acknowledged && data.round_id ? data : null);
    } catch {
      // Silent — banner just won't show on transient errors.
    }
  }, [eligible]);

  useEffect(() => {
    if (!eligible) return undefined;
    fetchCurrent();
    const id = setInterval(fetchCurrent, 30000);
    const onVis = () => { if (document.visibilityState === 'visible') fetchCurrent(); };
    const onFocus = () => fetchCurrent();
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onFocus);
    };
  }, [eligible, fetchCurrent]);

  const ack = async () => {
    if (!round?.round_id || busy) return;
    setBusy(true);
    try {
      await api.post(`/coal-round/${round.round_id}/ack`);
      setRound(null);
    } catch {
      // If ack fails, refresh state — maybe the round expired anyway.
      fetchCurrent();
    } finally {
      setBusy(false);
    }
  };

  if (!eligible || !round) return null;

  return (
    <div
      className="fixed bottom-4 right-4 z-[60] w-[calc(100%-2rem)] max-w-sm bg-gradient-to-br from-amber-500/95 to-amber-600/95 text-black rounded-sm shadow-2xl border border-amber-300/40 p-3 flex items-center gap-3 animate-fadeIn"
      data-testid="coal-round-banner"
    >
      <div className="w-9 h-9 rounded-sm bg-black/15 flex items-center justify-center shrink-0">
        <Flame className="w-4.5 h-4.5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-sans font-bold text-xs uppercase tracking-[0.15em] leading-tight">Coal round</p>
        <p className="font-sans text-xs leading-snug mt-0.5">Walk the floor — check every shisha table for fresh coals.</p>
      </div>
      <button
        onClick={ack}
        disabled={busy}
        className="shrink-0 h-9 px-3 rounded-sm bg-black text-amber-300 font-sans text-xs font-bold uppercase tracking-[0.15em] hover:bg-zinc-900 active:scale-95 transition disabled:opacity-50 flex items-center gap-1.5"
        data-testid="coal-round-done"
      >
        <Check className="w-3.5 h-3.5" />
        Done
      </button>
    </div>
  );
}
